import { Action, createActions } from '@common/types/actions';
import { DeliveryModal } from '@features/delivery/components/DeliveryModal';
import {
    MixerHorizontalIcon,
    PaperPlaneIcon,
    Share1Icon,
} from '@radix-ui/react-icons';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Clip } from '../types';

import { mapClipToOrderDeliveryItem } from '../utils/mapClipToOrderDeliveryItem';
import { mapClipToSendableItem } from '../utils/mapClipToSendableItem';
import { mapClipToShareableItem } from '../utils/mapClipToShareableItem';

import { SendDialog, useSendModal } from '@features/send';
import {
    ServiceOrderDialog,
    useServiceOrderModal,
} from '@features/serviceOrders';
import { ShareDialog, useShareModal } from '@features/share';
import { ServiceOrderProvider } from '@features/serviceOrders/context/ServiceOrderContext';

export const useClipActions = () => {
    const { t } = useTranslation(['clipTable']);
    const share = useShareModal();
    const send = useSendModal();
    const serviceOrder = useServiceOrderModal();

    const baseActions: Action<Clip>[] = useMemo(
        () =>
            createActions<Clip>([
                {
                    label: t('actions.share'),
                    color: 'gray',
                    icon: <Share1Icon />,
                    onClick: (items) =>
                        share.openShare(items.map(mapClipToShareableItem)),
                },
                {
                    label: t('actions.send'),
                    color: 'gray',
                    icon: <PaperPlaneIcon />,
                    onClick: (items) =>
                        send.openSend(items.map(mapClipToSendableItem)),
                },
                {
                    label: t('actions.requestService'),
                    color: 'gray',
                    icon: <MixerHorizontalIcon />,
                    onClick: (items) =>
                        serviceOrder.openServiceOrder(
                            items.map(mapClipToOrderDeliveryItem),
                        ),
                },
            ]),
        [send, serviceOrder, share, t],
    );

    const clipActionModals = (
        <>
            <DeliveryModal.Root value={share.dialogProps}>
                <ShareDialog />
            </DeliveryModal.Root>

            <DeliveryModal.Root value={send.dialogProps}>
                <SendDialog />
            </DeliveryModal.Root>

            <ServiceOrderProvider value={serviceOrder}>
                <DeliveryModal.Root value={serviceOrder.dialogProps}>
                    <ServiceOrderDialog />
                </DeliveryModal.Root>
            </ServiceOrderProvider>
        </>
    );

    return { baseActions, clipActionModals };
};
